// 📅 Havi trendek — regisztráció, előfizetők, bevétel (EUR), POI-fogyás
//   naptári hónaponként (Budapest). Adat: /api/monthly-trends.
import React, { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';

const card = { background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 12, padding: 16 };
const title = { fontSize: 13, color: '#8b8fa3', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 };
const btn = { padding: '8px 14px', borderRadius: 6, border: '1px solid #2a2e3a', background: '#0a0d14', color: '#e4e6eb', cursor: 'pointer', fontWeight: 600, fontSize: 12 };
const btnOn = { ...btn, background: '#00d4ff', color: '#0a0d14', borderColor: '#00d4ff' };

// Kategorikus színek rögzített sorrendben (a dataviz referencia-paletta sötét
//   lépcsői, a #1a1d27 háttérre validálva: CVD ΔE ≥ 9,4, kontraszt ≥ 3:1).
const C1 = '#3987e5'; // kék
const C2 = '#d95926'; // narancs
const C3 = '#199e70'; // türkiz
const GRID = '#2a2e3a', AXIS = '#8b8fa3';

const RANGES = [['12', '12 hónap'], ['24', '24 hónap'], ['all', 'Teljes']];
const nf = new Intl.NumberFormat('hu-HU');
const eur = (v) => (v == null ? '—' : new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'EUR', maximumFractionDigits: v >= 100 ? 0 : 2 }).format(v));
const tickMonth = (m, rows) => {
  const r = rows.find((x) => x.month === m);
  return `${m.slice(2, 4)}.${m.slice(5)}${r && r.partial ? '*' : ''}`;
};
const tooltipStyle = { contentStyle: { background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, fontSize: 12 }, labelStyle: { color: '#e4e6eb' }, itemStyle: { color: '#e4e6eb' }, cursor: { fill: 'rgba(255,255,255,0.04)' } };

function Tile({ label, value, sub }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 170 }}>
      <div style={title}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#e4e6eb' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#8b8fa3', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// Egy diagram-kártya. A folyó (részleges) hónap oszlopa halványabb.
function ChartCard({ label, note, rows, series, valueFmt = (v) => nf.format(v), axisFmt, height = 240 }) {
  const stacked = series.length > 1 && series[0].stack;
  return (
    <div style={{ ...card, flex: '1 1 460px', minWidth: 0 }}>
      <div style={title}>{label}</div>
      {note && <div style={{ fontSize: 11, color: '#8b8fa3', marginTop: -4, marginBottom: 8 }}>{note}</div>}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap={stacked || series.length === 1 ? '22%' : '12%'} barGap={2}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" stroke={AXIS} fontSize={11} tickFormatter={(m) => tickMonth(m, rows)} tickLine={false} />
            <YAxis stroke={AXIS} fontSize={11} tickFormatter={axisFmt || valueFmt} width={56} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip {...tooltipStyle} formatter={(v, n) => [valueFmt(v), n]}
              labelFormatter={(m) => { const r = rows.find((x) => x.month === m); return `${m}${r && r.partial ? ' (folyamatban)' : ''}`; }} />
            {/* A jelmagyarázat szövege semleges színű — az azonosítást a színes jel adja. */}
            {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: '#c9ccd6' }}>{v}</span>} />}
            {series.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} stackId={s.stack}
                stroke="#1a1d27" strokeWidth={stacked ? 1 : 0} maxBarSize={stacked || series.length === 1 ? undefined : 14}
                radius={!stacked || i === series.length - 1 ? [4, 4, 0, 0] : 0} isAnimationActive={false}>
                {rows.map((r) => <Cell key={r.month} fillOpacity={r.partial ? 0.45 : 1} />)}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Bevétel forrásonként + összevetés a Firestore-ban rögzített ellenőrzött
//   vásárlásokkal. A bolti elszámolás késik (Google Play: havi riport a hónap
//   után), a vásárlás-napló valós idejű — ha egy hónapban van Android-vásárlás,
//   de nincs Google Play-sor, az a hónap bevétele még hiányos.
const BASIS_SHORT = { actual: 'számla', modelled: '', mixed: 'vegyes' };
function RevenueCheck({ rows, meta }) {
  const gp = meta.googlePlay;
  const fmtTs = (v) => {
    if (!v) return '—';
    const d = new Date(/[TZ]/.test(v) ? v : String(v).replace(' ', 'T') + 'Z');
    return Number.isNaN(d.getTime()) ? String(v) : new Intl.DateTimeFormat('hu-HU', { timeZone: 'Europe/Budapest', dateStyle: 'short', timeStyle: 'short' }).format(d);
  };
  const cell = { padding: '5px 8px', borderBottom: '1px solid #20232d', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const amber = '#f59e0b';
  const list = rows.slice().reverse();
  // A Google Play havi elszámolása a folyó és az előző hónapra még hiányozhat;
  //   ennél régebbi hónapnál a hiány már hiba (pl. elmaradt letöltés).
  const recent = new Set(rows.slice(-2).map((r) => r.month));
  return (
    <div style={{ ...card, overflowX: 'auto' }}>
      <div style={title}>Bevétel forrásonként — ellenőrzés</div>
      <div style={{ fontSize: 11, color: '#8b8fa3', marginBottom: 8, lineHeight: 1.6 }}>
        Az App Store napi riportja másnapra megvan; a Google Play havi elszámolása csak a hónap lezárása után jelenik meg
        (jellemzően a következő hónap első felében), addig az Android-bevétel hiányzik.
        Összevetésül: a Firestore-ban rögzített, ellenőrzött vásárlások száma (valós idejű, teszt nélkül) és listaáras bruttó értékük.
        {gp && <> Google Play letöltés: <b style={{ color: gp.status === 'ok' ? '#22c55e' : amber }}>{gp.status}</b>, utolsó sikeres: {fmtTs(gp.lastSuccessAt)}{gp.status !== 'ok' && gp.message ? ` — ${gp.message}` : ''}.</>}
        {meta.purchasesError && <span style={{ color: amber }}> A vásárlás-napló nem olvasható: {meta.purchasesError}</span>}
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, color: '#e4e6eb' }}>
        <thead>
          <tr style={{ color: '#8b8fa3' }}>
            {['Hónap', 'App Store (nettó)', 'Google Play (nettó)', 'Összesen (nettó)', 'Vásárlás iOS', 'Vásárlás Android', 'Listaáras bruttó (becslés)']
              .map((h, i) => <th key={h} style={{ ...cell, textAlign: i === 0 ? 'left' : 'right', fontWeight: 600, borderBottom: '1px solid #2a2e3a' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            const rev = r.revenue || {};
            const p = r.purchases || {};
            const gpMissing = p.android > 0 && !rev.androidLoaded;
            const tag = (b) => (BASIS_SHORT[b] ? ` (${BASIS_SHORT[b]})` : '');
            return (
              <tr key={r.month} style={{ opacity: r.partial ? 0.75 : 1 }}>
                <td style={{ ...cell, textAlign: 'left' }}>{r.month}{r.partial ? '*' : ''}</td>
                <td style={cell}>{rev.iosNetEur != null ? eur(rev.iosNetEur) + tag(rev.iosBasis) : '—'}</td>
                <td style={gpMissing ? { ...cell, color: amber } : cell}
                  title={gpMissing ? (recent.has(r.month)
                    ? 'Erre a hónapra még nincs Google Play elszámolás, pedig volt Android-vásárlás — a hónap lezárása után jelenik meg.'
                    : 'Volt Android-vásárlás, de ehhez a hónaphoz nincs Google Play adat — az elszámolás letöltése elmaradt vagy hibázott.') : undefined}>
                  {gpMissing ? (recent.has(r.month) ? '⏳ elszámolás még nincs' : '⚠️ hiányzik') : (rev.androidNetEur != null ? eur(rev.androidNetEur) + tag(rev.androidBasis) : '—')}
                </td>
                <td style={cell}>{rev.netEur != null ? eur(rev.netEur) : '—'}{gpMissing ? <span style={{ color: amber }}> +?</span> : null}</td>
                <td style={cell}>{nf.format(p.ios || 0)}</td>
                <td style={cell}>{nf.format(p.android || 0)}</td>
                <td style={cell}>{p.listGrossEur != null ? eur(p.listGrossEur) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function MonthlyTrends() {
  const [range, setRange] = useState('12');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(false);

  const load = useCallback(async (r) => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`/api/monthly-trends?range=${r}`, { credentials: 'include' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setData(j);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(range); }, [range, load]);

  const exportXlsx = async () => {
    setExporting(true); setError(null);
    try {
      const res = await fetch(`/api/monthly-trends/export?range=${range}`, { credentials: 'include' });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data ? `havi_trendek_${data.from}_${data.to}.xlsx` : 'havi_trendek.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExporting(false); }
  };

  // Lapos sorok a diagramokhoz.
  const rows = (data?.rows || []).map((r) => ({
    month: r.month, partial: r.partial,
    ios: r.reg.ios, android: r.reg.android, other: r.reg.other,
    subscribers: r.subscribers,
    newActive: r.events?.newActive, churned: r.events?.churned, reactivated: r.events?.reactivated,
    subEur: r.revenue?.subscriptionEur ?? null, pkgEur: r.revenue?.packagesEur ?? null,
    consumed: r.poi.consumed, narrations: r.poi.narrations, consumers: r.poi.consumers,
    regTotal: r.reg.total, netEur: r.revenue?.netEur ?? null,
  }));
  const meta = data?.meta || {};
  const closed = rows.filter((r) => !r.partial);
  const last = closed[closed.length - 1];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ margin: 0, color: '#e4e6eb', fontSize: 20 }}>📅 Havi trendek</h2>

      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {RANGES.map(([k, l]) => (
          <button key={k} style={range === k ? btnOn : btn} onClick={() => setRange(k)} disabled={loading}>{l}</button>
        ))}
        <button style={{ ...btn, background: '#22c55e', color: '#0a0d14', borderColor: '#22c55e', marginLeft: 8 }}
          onClick={exportXlsx} disabled={!data || exporting || loading}>
          {exporting ? 'Készül…' : '📥 Excel'}
        </button>
        <button style={btn} onClick={() => setShowTable((v) => !v)} disabled={!data}>{showTable ? 'Diagramok' : 'Táblázat'}</button>
        {loading && <span style={{ color: '#8b8fa3', fontSize: 12 }}>Betöltés…</span>}
        {data && <span style={{ color: '#8b8fa3', fontSize: 12, marginLeft: 'auto' }}>{data.from} – {data.to} · naptári hónap, budapesti idő · * folyamatban lévő hónap</span>}
      </div>

      {error && <div style={{ ...card, borderColor: '#f43f5e', color: '#f43f5e', fontSize: 13 }}>Hiba: {error}</div>}

      {data && (
        <>
          {/* Figyelmeztetések az adatminőségről — ne higgyünk egy néma nullának. */}
          {meta.revenueReclass && !meta.revenueReclass.done && (
            <div style={{ ...card, borderColor: '#f59e0b', color: '#f59e0b', fontSize: 12 }}>
              ⚠️ A bolti bevételek újratöltése (termékbesorolás és App Store darabszám-javítás) még nem fejeződött be — addig a bevételi számok a régi, hiányos számítást mutathatják.
            </div>
          )}
          {!meta.subscriptionCreditsSeen && (
            <div style={{ ...card, color: '#8b8fa3', fontSize: 12 }}>
              Az időszakban nincs előfizetői (subscription) POI-jóváírás. Vagy nem volt előfizető, vagy az app más forrásnévvel könyveli — ilyenkor az előfizetők száma 0-nak látszik.
            </div>
          )}

          {last && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <Tile label={`Regisztráció · ${last.month}`} value={nf.format(last.regTotal)} sub={`iOS ${last.ios} · Android ${last.android} · egyéb ${last.other}`} />
              <Tile label={`Előfizetők · ${last.month}`} value={nf.format(last.subscribers)}
                sub="előfizetői POI-jóváírás alapján" />
              <Tile label={`Bevétel (nettó) · ${last.month}`} value={eur(last.netEur)}
                sub={last.subEur != null ? `előfizetés ${eur(last.subEur)} · POI-csomag ${eur(last.pkgEur)}` : 'nincs bevételi adat'} />
              <Tile label={`POI fogyás · ${last.month}`} value={nf.format(last.consumed)} sub={`${nf.format(last.consumers)} felhasználó · ${nf.format(last.narrations)} narráció`} />
            </div>
          )}

          {showTable ? (
            <div style={{ ...card, overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, color: '#e4e6eb' }}>
                <thead>
                  <tr style={{ color: '#8b8fa3', textAlign: 'right' }}>
                    {['Hónap', 'Regisztráció', 'iOS', 'Android', 'egyéb', 'Előfizetők', ...(meta.eventsAvailable ? ['Új', 'Lemondott', 'Visszatért'] : []), 'Előfizetés €', 'POI-csomag €', 'Elfogyasztott POI', 'Narrációk', 'POI-t használók']
                      .map((h, i) => <th key={h} style={{ padding: '6px 8px', borderBottom: '1px solid #2a2e3a', textAlign: i === 0 ? 'left' : 'right', fontWeight: 600 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.month} style={{ opacity: r.partial ? 0.6 : 1 }}>
                      {[`${r.month}${r.partial ? '*' : ''}`, r.regTotal, r.ios, r.android, r.other, r.subscribers,
                        ...(meta.eventsAvailable ? [r.newActive, r.churned, r.reactivated] : []),
                        eur(r.subEur), eur(r.pkgEur), r.consumed, r.narrations, r.consumers]
                        .map((v, i) => <td key={i} style={{ padding: '5px 8px', borderBottom: '1px solid #20232d', textAlign: i === 0 ? 'left' : 'right', fontVariantNumeric: 'tabular-nums' }}>{typeof v === 'number' ? nf.format(v) : v}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <ChartCard label="Regisztrációk havonta" rows={rows}
                series={[{ key: 'ios', name: 'iOS', color: C1, stack: 'r' }, { key: 'android', name: 'Android', color: C2, stack: 'r' }, { key: 'other', name: 'egyéb', color: C3, stack: 'r' }]} />
              <ChartCard label="Előfizetők havonta" rows={rows}
                note="Akik a hónapban előfizetői POI-jóváírást kaptak. A havi előfizetést az app kivezette — azóta 0 a várt érték."
                series={[{ key: 'subscribers', name: 'Előfizetők', color: C1 }]} />
              {meta.eventsAvailable && (
                <ChartCard label="Új és lemondott előfizetések" rows={rows} note="subscription_events alapján"
                  series={[{ key: 'newActive', name: 'Új', color: C1 }, { key: 'churned', name: 'Lemondott', color: C2 }, { key: 'reactivated', name: 'Visszatért', color: C3 }]} />
              )}
              <ChartCard label="Bevétel havonta (nettó, EUR)" rows={rows}
                note="Ugyanaz a számítás, mint a Pénzügy (pályázat) oldalon: InforEuro árfolyam, Google-jutalék levonva, számla ahol van."
                valueFmt={(v) => eur(v)} axisFmt={(v) => `${nf.format(v)} €`}
                series={[{ key: 'subEur', name: 'Előfizetés', color: C1, stack: 'e' }, { key: 'pkgEur', name: 'POI-csomag', color: C2, stack: 'e' }]} />
              <ChartCard label="Elfogyasztott POI havonta" rows={rows}
                series={[{ key: 'consumed', name: 'Elfogyasztott POI', color: C1 }]} />
              <ChartCard label="POI-t használó felhasználók havonta" rows={rows}
                series={[{ key: 'consumers', name: 'Felhasználók', color: C1 }]} />
            </div>
          )}

          <RevenueCheck rows={data.rows} meta={meta} />
        </>
      )}
    </div>
  );
}
